import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";

import "./Sidebar.scss";
import NavBarViewModel from "../../ui/NavBar/NavBarViewModel";
import AppT9N from "../../../t9n/App/App.json";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import { VNode } from "src/interfaces/interfaces";
import {
  isEnterprise,
  isMobileView,
  isRTL,
  keyboardAccessibleHelper,
} from "../../utils/appUtils";
import FeaturedSidebar, { PRODUCT_NAME } from "./FeaturedSidebar";
import MyAppsSidebar from "./MyAppsSidebar";
import { substitute } from "@arcgis/core/intl";
import {
  logSuggestionAnswerEvent,
  logSuggestionFeedbackEvent,
} from "../../utils/telemetryUtils";

interface ISidebarTitle {
  title: string;
  useDesc: boolean;
  desc?: string;
  descTooltip?: string;
}

@subclass("Landing.widgets.Sidebar")
export default class Sidebar extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  filteredTemplateList: EAppTemplateType[];

  @property()
  firstAnswerTemplates: EAppTemplateType[];

  @property()
  secondAnswerTemplates: EAppTemplateType[];

  @property()
  thirdAnswerTemplates: EAppTemplateType[];

  @property()
  featuredSidebar: FeaturedSidebar;

  @property()
  myAppsSidebar: MyAppsSidebar;

  _secondQuestion: QuestionInfo;
  _thirdQuestion: QuestionInfo;

  _firstQuestionDOM: any;
  _secondQuestionDOM: any;
  _thirdQuestionDOM: any;

  _selectionAnswersState: {
    "0"?: number; // answerIndex number
    "1"?: number; // answerIndex number
    "2"?: number; // answerIndex number
  } = {};

  _currentFocusDepth: number = null;
  _shouldShowTerminalMsg: boolean = false;

  sidebarCloseBtn: HTMLCalciteButtonElement;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  postInitialize() {
    this.featuredSidebar = new FeaturedSidebar({ navbarVM: this.navbarVM });
    this.myAppsSidebar = new MyAppsSidebar({ navbarVM: this.navbarVM });

    this.watch(
      ["firstAnswerTemplates", "secondAnswerTemplates", "thirdAnswerTemplates"],
      () => {
        this._calcFilterList();
      }
    );

    this.navbarVM.watch(["suggestionsButtonSignal", "navigationRoute"], () => {
      this._calcFilterList();
    });

    this.navbarVM.watch(["suggestionsButtonSignal"], () => {
      if (this.navbarVM.suggestionsButtonSignal === true) {
        setTimeout(() => {
          this.sidebarCloseBtn?.focus();
          this.sidebarCloseBtn?.setFocus();
        }, 250);
      }
    });

    this.watch("filteredTemplateList", () => {
      this.navbarVM.uniqueRenderingTimeStamp =
        this.navbarVM.getUniqueTimeStamp();
    });
  }

  render() {
    return (
      <div
        key="sidebar-area"
        id="sidebar-area"
        class={this.classes(
          "sidebar-area",
          this.navbarVM.suggestionsButtonSignal ? "sidebar-area-expanded" : ""
        )}
      >
        {this.navbarVM.suggestionsButtonSignal && this._sidebarRouter()}
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _sidebarRouter(): VNode {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        return this._renderBrowseAll();
      case "featured":
        return this._renderFeatured();
      case "myApps":
        return this._renderMyApps();
      default:
        return null;
    }
  }

  private _renderBrowseAll(): VNode {
    const { suggestions, desc } = this.appMessages.suggestions;
    const tip = this.appMessages.suggestions["2ndTip"];
    return [
      this._renderTitle({
        title: suggestions,
        useDesc: true,
        desc,
        descTooltip: tip,
      }),
      this._renderOptions(),
    ];
  }

  private _renderFeatured(): VNode {
    return [
      this._renderTitle({
        title: substitute(this.appMessages.featuredTab.leftSidebarTitle, {
          productName: PRODUCT_NAME,
        }),
        useDesc: false,
      }),
      this?.featuredSidebar?.render(),
    ];
  }

  private _renderMyApps(): VNode {
    return [
      this._renderTitle({
        title: this.appMessages.myAppsTabSidebar.filters,
        useDesc: false,
      }),
      this?.myAppsSidebar?.render(),
    ];
  }

  private _renderGroupResourceType(): VNode {
    const { groupResource } = this.appMessages.suggestions;
    return (
      <div key="groupResource" class="go-to-browseAll">
        <div>{groupResource}</div>
      </div>
    );
  }

  private _calcFilterList(): void {
    if (
      (this.navbarVM.suggestionsButtonSignal === false && !isMobileView()) || // if the sidebar is closed and the app is not in mobile view
      this.navbarVM.navigationRoute !== "browseAll"
    ) {
      this.filteredTemplateList = null;
    } else {
      this.filteredTemplateList =
        this.thirdAnswerTemplates ||
        this.secondAnswerTemplates ||
        this.firstAnswerTemplates;
    }
  }

  private _renderTitle(titleInfo: ISidebarTitle) {
    const { title, useDesc, desc, descTooltip } = titleInfo;
    const toggleVis = () => {
      this.navbarVM.toggleSuggBtnSignal();
    };
    return (
      <div id="sugg-title">
        <div>
          <h2>{title}</h2>
          {useDesc && (
            <p>
              {desc}
              &nbsp;
              <calcite-icon
                scale="s"
                tabindex="0"
                icon="information"
                class="info-icon"
                id="info-icon"
              ></calcite-icon>
              <calcite-tooltip
                label={descTooltip}
                placement="bottom"
                reference-element="info-icon"
              >
                {descTooltip}
              </calcite-tooltip>
            </p>
          )}
        </div>

        <div class="x-btn">
          <calcite-button
            onclick={toggleVis}
            icon-start="x"
            kind="neutral"
            appearance="transparent"
            afterCreate={storeNode}
            bind={this}
            data-node-ref="sidebarCloseBtn"
            scale="l"
          ></calcite-button>
        </div>
      </div>
    );
  }

  private _renderOptions() {
    const rootQuestion: QuestionInfo = this._decideRootQuestion();
    this._firstQuestionDOM = this._renderQuestion(rootQuestion, 0);
    this._secondQuestionDOM = this._renderQuestion(this._secondQuestion, 1);
    this._thirdQuestionDOM = this._renderQuestion(this._thirdQuestion, 2);
    return (
      <div key="questions-section" class={this.classes("questions-section")}>
        {this._firstQuestionDOM}
        {this._secondQuestionDOM}
        {this._thirdQuestionDOM}
        {this._shouldShowTerminalMsg ? this._renderTerminalMsg() : null}
      </div>
    );
  }

  private _renderTerminalMsg(): VNode {
    const { terminalResponse } = this.appMessages.suggestions;
    return [
      <div class="terminal-message" key="terminal-message-key">
        <calcite-icon
          scale="l"
          class="terminal-icon"
          icon="check-circle-f"
        ></calcite-icon>
        {terminalResponse}
      </div>,
      !isEnterprise(this.navbarVM.portal) ? this._renderFeedback() : null,
    ];
  }

  @property()
  rating: HTMLCalciteRatingElement;
  @property()
  feedbackText: HTMLCalciteInputElement;
  @property()
  _showSuccess: boolean = false;

  /** Minimum Required info for submit */
  get feedbackSubmitDisabled(): boolean {
    return this?.rating?.value === 0 || this?.rating?.value == null;
  }

  @property()
  _isFeedbackDisabled: boolean = true;

  private _renderFeedback() {
    const { title, prompt, placeholder, submit, success } =
      this.appMessages.feedback;
    return (
      <div class="provide-feedback" key="provide-feedback-key">
        <calcite-accordion>
          <calcite-accordion-item
            afterCreate={(domRef) => {
              setTimeout(() => {
                domRef?.focus();
              }, 500);
            }}
            icon-start="book"
            heading={title}
          >
            <div id="feedback-wrapper">
              <calcite-label>
                {prompt}
                <calcite-rating
                  bind={this}
                  data-node-ref="rating"
                  afterCreate={storeNode}
                  onCalciteRatingChange={() => {
                    this._isFeedbackDisabled = this.feedbackSubmitDisabled;
                  }}
                ></calcite-rating>
              </calcite-label>
              <calcite-input
                bind={this}
                data-node-ref="feedbackText"
                afterCreate={storeNode}
                type="textarea"
                placeholder={placeholder}
                maxLength={300}
                onCalciteInputInput={() => {
                  this._isFeedbackDisabled = this.feedbackSubmitDisabled;
                }}
              ></calcite-input>
              <div id="submit-feedback-div">
                <calcite-button
                  id="submit-feedback"
                  afterCreate={() => {
                    this._isFeedbackDisabled = this.feedbackSubmitDisabled;
                  }}
                  disabled={this._isFeedbackDisabled}
                  onclick={() => {
                    logSuggestionFeedbackEvent(
                      this.navbarVM.telemetry,
                      this?.rating?.value,
                      this?.feedbackText?.value
                    );
                    this.rating.value = 0;
                    this.feedbackText.value = "";

                    this._showSuccess = true;
                    this._isFeedbackDisabled = this.feedbackSubmitDisabled;

                    setTimeout(() => {
                      this._showSuccess = false;
                    }, 2000);
                  }}
                >
                  {submit}
                </calcite-button>
                {this._showSuccess && <p id="success">{success}</p>}
              </div>
            </div>
          </calcite-accordion-item>
        </calcite-accordion>
      </div>
    );
  }

  /** All the same RootQuestion for now, but could change in the future */
  private _decideRootQuestion(): QuestionInfo {
    return this._constructQuestionTree_NoResource();
    // const { loadedItemType } = this.navbarVM.itemLoaderVM;
    //   if (loadedItemType === "none") {
    //   return this._constructQuestionTree_NoResource();
    // } else if (loadedItemType === "webmap") {
    //   return this._constructQuestionTree_NoResource();
    // } else if (loadedItemType === "webscene") {
    //   return this._constructQuestionTree_NoResource();
    // }
  }

  private _sendFocusToQuestionHelper(questionVNodeRef: string) {
    if (questionVNodeRef === null) {
      return;
    }
    setTimeout(() => {
      this?.[questionVNodeRef]?.children?.[1]?.children?.[0]?.domNode?.focus();
    }, 400);
  }

  private _clickOnAnswer(
    answerInfo: AnswerInfo,
    depth: number,
    answerIndex: number,
    originalQuestion: QuestionInfo
  ) {
    const { nextQuestion, resultingApps } = answerInfo;
    this._currentFocusDepth = null;
    if (depth === 0) {
      this.resetDepthTo0();
      this._secondQuestion = nextQuestion;
      this.firstAnswerTemplates = resultingApps;
      this._sendFocusToQuestionHelper("_secondQuestionDOM");
    } else if (depth === 1) {
      this.resetDepthTo1();
      this._thirdQuestion = nextQuestion;
      this.secondAnswerTemplates = resultingApps;
      this._sendFocusToQuestionHelper("_thirdQuestionDOM");
    } else if (depth === 2) {
      this.thirdAnswerTemplates = resultingApps;
    }

    logSuggestionAnswerEvent(
      this.navbarVM.telemetry,
      depth,
      originalQuestion,
      answerInfo
    );

    this._selectionAnswersState[depth] = answerIndex;

    this._shouldShowTerminalMsg = nextQuestion == null;
  }

  resetDepthTo0() {
    this._secondQuestion = null;
    this._thirdQuestion = null;

    this.firstAnswerTemplates = null;
    this.secondAnswerTemplates = null;
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[0] = null;
    this._selectionAnswersState[1] = null;
    this._selectionAnswersState[2] = null;
  }

  resetDepthTo1() {
    this._thirdQuestion = null;

    this.secondAnswerTemplates = null;
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[1] = null;
    this._selectionAnswersState[2] = null;
  }

  resetDepthTo2() {
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[2] = null;
  }

  private _renderQuestion(question: QuestionInfo, depth: number): VNode {
    if (question == null) {
      return null;
    }

    const currAnswerState: number = this._selectionAnswersState[depth];
    if (currAnswerState == null || depth === this._currentFocusDepth) {
      return this._renderActiveQuestion(question, depth);
    } else {
      return this._renderAnsweredQuestion(question, depth);
    }
  }

  _clearAction = (depth: number) => {
    this._shouldShowTerminalMsg = false;
    switch (depth) {
      case 0:
        this.resetDepthTo0();
        this._sendFocusToQuestionHelper("_firstQuestionDOM");
        break;
      case 1:
        this.resetDepthTo1();
        this._sendFocusToQuestionHelper("_secondQuestionDOM");
        break;
      case 2:
        this.resetDepthTo2();
        this._sendFocusToQuestionHelper("_thirdQuestionDOM");
    }
  };

  private _renderActiveQuestion(question: QuestionInfo, depth: number) {
    const formattedKey = question.questionText.split(" ").join("");
    const questionNumber = depth + 1;

    return (
      <div key={formattedKey} class="question-tree">
        <div class="question">
          <span>
            {questionNumber}. {question.questionText}
          </span>
          {this._selectionAnswersState[depth] != null ? (
            <calcite-icon
              scale="m"
              class="clear-icon"
              icon="x"
              tabindex="0"
              onclick={this._clearAction.bind(this, depth)}
              onkeyup={(e) => {
                keyboardAccessibleHelper(
                  this._clearAction.bind(this, depth),
                  e
                );
              }}
            ></calcite-icon>
          ) : null}
        </div>
        <div class="answers">
          {question.answers.map((answerInfo: AnswerInfo, index: number) => {
            const selectAction = this._clickOnAnswer.bind(
              this,
              answerInfo,
              depth,
              index,
              question
            );

            return (
              <div
                class="answer"
                key={`${formattedKey}-answer-${index}`}
                tabindex="0"
                onclick={selectAction}
                onkeyup={(e) => {
                  keyboardAccessibleHelper(selectAction, e);
                }}
              >
                <calcite-icon
                  scale="m"
                  class="answer-icon"
                  icon={
                    this._selectionAnswersState[depth] === index
                      ? "check"
                      : "bullet-point"
                  }
                ></calcite-icon>
                &nbsp;
                <div>{answerInfo.answerText}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  private _renderAnsweredQuestion(question: QuestionInfo, depth: number) {
    const formattedKey = question.questionText.split(" ").join("");
    const questionNumber = depth + 1;
    return (
      <div key={`${formattedKey}-ANSWERED`} class="answered-question">
        <div class="question">
          <span>
            {questionNumber}. {question.questionText}
          </span>
          <calcite-icon
            scale="m"
            class="clear-icon"
            icon="x"
            tabindex="0"
            onclick={this._clearAction.bind(this, depth)}
            onkeyup={(e) => {
              keyboardAccessibleHelper(this._clearAction.bind(this, depth), e);
            }}
          ></calcite-icon>
        </div>
        <div class="answers">
          {question.answers.map((answerInfo: AnswerInfo, index: number) => {
            const chgFocusDepth = (depth: number) => {
              this._currentFocusDepth = depth;
              this._sendFocusToQuestionHelper(
                this._currentFocusDepth === 0
                  ? "_firstQuestionDOM"
                  : this._currentFocusDepth === 1
                  ? "_secondQuestionDOM"
                  : this._currentFocusDepth === 2
                  ? "_thirdQuestionDOM"
                  : null
              );
            };
            return this._selectionAnswersState[depth] === index ? (
              <div
                class="answer"
                key={`${formattedKey}-picked-answer`}
                onclick={() => {
                  chgFocusDepth.call(this, depth);
                }}
                onkeyup={(e) => {
                  keyboardAccessibleHelper(chgFocusDepth.bind(this, depth), e);
                }}
                tabindex="0"
              >
                <div class="answer-text">
                  <calcite-icon
                    scale="m"
                    class="answer-icon"
                    icon="check"
                  ></calcite-icon>

                  <div>{answerInfo.answerText}</div>
                </div>

                <calcite-icon
                  scale="m"
                  class="reanswer-icon"
                  icon="chevron-up"
                ></calcite-icon>
              </div>
            ) : null;
          })}
        </div>
      </div>
    );
  }

  private _constructQuestionTree_NoResource(): QuestionInfo {
    const {
      Q_What_is_your_goal_for_the_app,
      A_Showcase_one_or_more_maps_with_essential_tools,
      A_Allow_viewers_to_interact_with_your_data,
      A_Provide_public_information,
      A_Display_a_variety_of_features_and_content_types,
      Q_What_do_you_want_to_display_in_the_app,
      A_One_web_map,
      A_One_web_scene,
      A_Multiple_maps_and_scenes,
      Q_Does_the_map_include_an_image_service,
      A_Yes,
      A_No,

      Q_What_do_you_want_viewers_to_do_with_your_data,
      A_Measure_distance,
      A_Compare_maps_and_data,
      A_Filter_features,
      A_Edit_data,
      A_Explore_graphic_and_statistical_information,
      Q_What_type_of_data_do_you_have,
      A_Web_Scene,
      A_Web_Map,
      A_Map_with_Image_Service,
      Q_What_do_you_want_to_compare,
      A_Maps,
      A_Layers,
      A_Features,
      Q_Does_your_map_include_time_enabled_data,
      Q_What_do_you_want_to_display,
      A_Charts_saved_in_a_map,
      A_Ranked_lists_of_features,
      A_Time_animation,
      Q_What_do_you_want_to_share,
      A_Images_stored_as_feature_attachments,
      A_Graphic_and_statistical_information,
      A_Proximity_to_a_feature,
      A_ArcGIS_items_and_other_resources,
      Q_What_is_the_feature_geometry_type,
      A_Points_or_lines,
      A_Polygons,
      Q_Is_the_content_shared_to_a_group,
      A_Bookmarked_locations,
    } = this.appMessages.suggestions.qaCollection;
    return {
      questionText: Q_What_is_your_goal_for_the_app,
      answers: [
        {
          answerText: A_Showcase_one_or_more_maps_with_essential_tools,
          resultingApps: [
            EAppTemplateType.ThreeDViewer,
            EAppTemplateType.Atlas,
            EAppTemplateType.Basic,
            EAppTemplateType.Compare,
            EAppTemplateType.Exhibit,
            EAppTemplateType.ImageryApp,
            EAppTemplateType.Insets,
            EAppTemplateType.Media,
            EAppTemplateType.Minimalist,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Sidebar,
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_display_in_the_app,
            answers: [
              {
                answerText: A_One_web_map,
                resultingApps: [
                  EAppTemplateType.Basic,
                  EAppTemplateType.Exhibit,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Insets,
                  EAppTemplateType.Media,
                  EAppTemplateType.Minimalist,
                  EAppTemplateType.Sidebar,
                ],
                nextQuestion: {
                  questionText: Q_Does_the_map_include_an_image_service,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [EAppTemplateType.ImageryApp],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_No,
                      resultingApps: [
                        EAppTemplateType.Basic,
                        EAppTemplateType.Exhibit,
                        EAppTemplateType.Insets,
                        EAppTemplateType.Media,
                        EAppTemplateType.Minimalist,
                        EAppTemplateType.Sidebar,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_One_web_scene,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.Basic,
                  EAppTemplateType.Exhibit,
                ],
                nextQuestion: null,
              },
              {
                answerText: A_Multiple_maps_and_scenes,
                resultingApps: [
                  EAppTemplateType.Atlas,
                  EAppTemplateType.Compare,
                  EAppTemplateType.Portfolio,
                  EAppTemplateType.Manager,
                ],
                nextQuestion: null,
              },
            ],
          },
        },
        {
          answerText: A_Allow_viewers_to_interact_with_your_data,
          resultingApps: [
            EAppTemplateType.ThreeDViewer,
            EAppTemplateType.AttachmentViewer,
            EAppTemplateType.Charts,
            EAppTemplateType.Compare,
            EAppTemplateType.Countdown,
            EAppTemplateType.ImageryApp,
            EAppTemplateType.Insets,
            EAppTemplateType.InteractiveLegend,
            EAppTemplateType.Manager,
            EAppTemplateType.Media,
            EAppTemplateType.Nearby,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Observer,
            EAppTemplateType.Reporter,
            EAppTemplateType.Sidebar,
            EAppTemplateType.Slider,
            EAppTemplateType.Streamflow,
            EAppTemplateType.ZoneLookup,
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_viewers_to_do_with_your_data,
            answers: [
              {
                answerText: A_Measure_distance,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Media,
                  EAppTemplateType.Observer,
                  EAppTemplateType.Sidebar,
                ],
                nextQuestion: {
                  questionText: Q_What_type_of_data_do_you_have,
                  answers: [
                    {
                      answerText: A_Web_Scene,
                      resultingApps: [
                        EAppTemplateType.ThreeDViewer,
                        EAppTemplateType.Observer,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Web_Map,
                      resultingApps: [
                        EAppTemplateType.Media,
                        EAppTemplateType.Sidebar,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Map_with_Image_Service,
                      resultingApps: [EAppTemplateType.ImageryApp],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_Compare_maps_and_data,
                resultingApps: [
                  EAppTemplateType.Compare,
                  EAppTemplateType.Countdown,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Insets,
                  EAppTemplateType.Manager,
                  EAppTemplateType.Media,
                  EAppTemplateType.Portfolio,
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_compare,
                  answers: [
                    {
                      answerText: A_Maps,
                      resultingApps: [
                        EAppTemplateType.Compare,
                        EAppTemplateType.Portfolio,
                        EAppTemplateType.Manager,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Features,
                      resultingApps: [
                        EAppTemplateType.Compare,
                        EAppTemplateType.Countdown,
                        EAppTemplateType.Insets,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Layers,
                      resultingApps: [
                        EAppTemplateType.Compare,
                        EAppTemplateType.ImageryApp,
                        EAppTemplateType.Media,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_Filter_features,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.AttachmentViewer,
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Manager,
                  EAppTemplateType.Media,
                  EAppTemplateType.Nearby,
                  EAppTemplateType.Reporter,
                  EAppTemplateType.Sidebar,
                  EAppTemplateType.Slider,
                  EAppTemplateType.ZoneLookup,
                ],
                nextQuestion: {
                  questionText: Q_Does_your_map_include_time_enabled_data,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [
                        EAppTemplateType.ThreeDViewer,
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Media,
                        EAppTemplateType.Slider,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_No,
                      resultingApps: [
                        EAppTemplateType.ThreeDViewer,
                        EAppTemplateType.AttachmentViewer,
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Manager,
                        EAppTemplateType.Media,
                        EAppTemplateType.Nearby,
                        EAppTemplateType.Reporter,
                        EAppTemplateType.Sidebar,
                        EAppTemplateType.ZoneLookup,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_Edit_data,
                resultingApps: [
                  EAppTemplateType.AttachmentViewer,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Manager,
                  EAppTemplateType.Reporter,
                  EAppTemplateType.Sidebar,
                ],
                nextQuestion: null,
              },
              {
                answerText: A_Explore_graphic_and_statistical_information,
                resultingApps: [
                  EAppTemplateType.Charts,
                  EAppTemplateType.Countdown,
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Slider,
                  EAppTemplateType.Streamflow,
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_display,
                  answers: [
                    {
                      answerText: A_Charts_saved_in_a_map,
                      resultingApps: [
                        EAppTemplateType.Charts,
                        EAppTemplateType.Streamflow,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Ranked_lists_of_features,
                      resultingApps: [EAppTemplateType.Countdown],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Time_animation,
                      resultingApps: [
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Slider,
                        EAppTemplateType.Streamflow,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
            ],
          },
        },
        {
          answerText: A_Provide_public_information,
          resultingApps: [
            EAppTemplateType.ThreeDViewer,
            EAppTemplateType.Atlas,
            EAppTemplateType.AttachmentViewer,
            EAppTemplateType.CategoryGallery,
            EAppTemplateType.Charts,
            EAppTemplateType.Countdown,
            EAppTemplateType.Nearby,
            EAppTemplateType.Notify,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Reporter,
            EAppTemplateType.Slider,
            EAppTemplateType.Streamflow,
            EAppTemplateType.ZoneLookup,
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_share,
            answers: [
              {
                answerText: A_Images_stored_as_feature_attachments,
                resultingApps: [EAppTemplateType.AttachmentViewer],
                nextQuestion: null,
              },
              {
                answerText: A_Graphic_and_statistical_information,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.Charts,
                  EAppTemplateType.Countdown,
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Slider,
                  EAppTemplateType.Streamflow,
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_display,
                  answers: [
                    {
                      answerText: A_Charts_saved_in_a_map,
                      resultingApps: [EAppTemplateType.Charts],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Ranked_lists_of_features,
                      resultingApps: [EAppTemplateType.Countdown],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Time_animation,
                      resultingApps: [
                        EAppTemplateType.ThreeDViewer,
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Slider,
                        EAppTemplateType.Streamflow,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_Proximity_to_a_feature,
                resultingApps: [
                  EAppTemplateType.Nearby,
                  EAppTemplateType.Notify,
                  EAppTemplateType.Reporter,
                  EAppTemplateType.ZoneLookup,
                ],
                nextQuestion: {
                  questionText: Q_What_is_the_feature_geometry_type,
                  answers: [
                    {
                      answerText: A_Points_or_lines,
                      resultingApps: [
                        EAppTemplateType.Nearby,
                        EAppTemplateType.Notify,
                        EAppTemplateType.Reporter,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_Polygons,
                      resultingApps: [
                        EAppTemplateType.Notify,
                        EAppTemplateType.ZoneLookup,
                      ],
                      nextQuestion: null,
                    },
                  ],
                },
              },
              {
                answerText: A_ArcGIS_items_and_other_resources,
                resultingApps: [
                  EAppTemplateType.Atlas,
                  EAppTemplateType.CategoryGallery,
                  EAppTemplateType.Portfolio,
                ],
                nextQuestion: {
                  questionText: Q_Is_the_content_shared_to_a_group,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [
                        EAppTemplateType.Atlas,
                        EAppTemplateType.CategoryGallery,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_No,
                      resultingApps: [EAppTemplateType.Portfolio],
                      nextQuestion: null,
                    },
                  ],
                },
              },
            ],
          },
        },
        {
          answerText: A_Display_a_variety_of_features_and_content_types,
          resultingApps: [
            EAppTemplateType.Atlas,
            EAppTemplateType.AttachmentViewer,
            EAppTemplateType.CategoryGallery,
            EAppTemplateType.Charts,
            EAppTemplateType.Compare,
            EAppTemplateType.Exhibit,
            EAppTemplateType.Insets,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Streamflow,
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_display,
            answers: [
              {
                answerText: A_Images_stored_as_feature_attachments,
                resultingApps: [EAppTemplateType.AttachmentViewer],
                nextQuestion: null,
              },
              {
                answerText: A_Charts_saved_in_a_map,
                resultingApps: [EAppTemplateType.Charts],
                nextQuestion: null,
              },
              {
                answerText: A_Bookmarked_locations,
                resultingApps: [
                  EAppTemplateType.Compare,
                  EAppTemplateType.Exhibit,
                  EAppTemplateType.Insets,
                  EAppTemplateType.Streamflow,
                ],
                nextQuestion: null,
              },
              {
                answerText: A_ArcGIS_items_and_other_resources,
                resultingApps: [
                  EAppTemplateType.Atlas,
                  EAppTemplateType.CategoryGallery,
                  EAppTemplateType.Portfolio,
                ],
                nextQuestion: {
                  questionText: Q_Is_the_content_shared_to_a_group,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [
                        EAppTemplateType.Atlas,
                        EAppTemplateType.CategoryGallery,
                      ],
                      nextQuestion: null,
                    },
                    {
                      answerText: A_No,
                      resultingApps: [EAppTemplateType.Portfolio],
                      nextQuestion: null,
                    },
                  ],
                },
              },
            ],
          },
        },
      ],
    };
  }

  private _constructQuestionTree_Webmap(): QuestionInfo {
    return null;
  }

  private _constructQuestionTree_Webscene(): QuestionInfo {
    return null;
  }
}

export interface QuestionInfo {
  questionText: string;
  answers: AnswerInfo[];
}

export interface AnswerInfo {
  answerText: string;
  resultingApps: EAppTemplateType[];
  nextQuestion: QuestionInfo | null; // null implies you're at a deadend in the tree
}
